"use client";

import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import { Wallet, Building2, Calendar, CreditCard, Hash, Undo2, Save, FileText, User } from "lucide-react";
import PageHeader from "@/components/layout/PageHeader";
import { notify } from "@/components/ui/dialogs";
import { get, post, can, getCurrentOrganization } from "@/utils/service";
import { apiMessage, fieldErrors, money, label, shortDate } from "@/utils/api";
import FloatingDrawer, { DrawerHeader, DetailRow, darkInput, darkLabel, darkPrimary, darkSecondary } from "@/components/crm/FloatingDrawer";
import ListShell, { toolbarInput, pill } from "@/components/crm/ListShell";
import { useCounts, useApiData, countText } from "@/components/crm/useCounts";
import RecordSelect, { type Picked } from "@/components/crm/RecordSelect";
import { useRemoteList } from "@/components/crm/useRemoteList";
import AppSelect from "@/components/ui/app-select";
import SearchInput from "@/components/ui/search-input";
import DateInput from "@/components/ui/date-input";

interface Payment {
  id: string; amount: string; currency: string; status: string; paid_date: string; reference: string | null; notes: string | null; method: string | null;
  refunded_amount: string; unallocated_amount: string; allocated_amount: string; provider: string | null;
  account?: { id: string; name: string } | null; payment_method?: { name: string } | null; recorder?: { first_name: string; last_name: string } | null;
  allocations?: Array<{ id: string; amount: string; invoice?: { id: string; invoice_number: string; currency: string } | null }>;
}
interface OpenInvoice { id: string; invoice_number: string; balance: string; currency: string; due_date: string }
interface Method { id: string; name: string }

const STATUS: Record<string, string> = {
  succeeded: "bg-green-100 text-green-800", pending: "bg-amber-100 text-amber-800", failed: "bg-red-100 text-red-800",
  cancelled: "bg-gray-100 text-gray-600", refunded: "bg-gray-100 text-gray-700", partially_refunded: "bg-blue-100 text-blue-800",
};
const STATUS_LABEL: Record<string, string> = { succeeded: "Received", pending: "Pending", failed: "Failed", cancelled: "Cancelled", refunded: "Refunded", partially_refunded: "Partly refunded" };

function useOpenInvoices(accountId: string | null, currency: string) {
  const [rows, setRows] = useState<OpenInvoice[]>([]);
  useEffect(() => {
    if (!accountId) { setRows([]); return; }
    Promise.all(["unpaid", "partially_paid", "overdue"].map((s) => get<{ data: OpenInvoice[] }>(`/invoices?account_id=${accountId}&status=${s}&currency=${currency}&page_size=100`).then((r) => r.data).catch(() => [])))
      .then((lists) => setRows(lists.flat().filter((i, idx, all) => all.findIndex((x) => x.id === i.id) === idx)));
  }, [accountId, currency]);
  return rows;
}

function RecordPayment({ isOpen, onClose, onSaved }: { isOpen: boolean; onClose: () => void; onSaved: () => void }) {
  const base = getCurrentOrganization()?.base_currency || "UGX";
  const [account, setAccount] = useState<Picked>(null);
  const [form, setForm] = useState({ amount: "", currency: base, paid_date: new Date().toISOString().slice(0, 10), payment_method_id: "", reference: "", notes: "" });
  const [alloc, setAlloc] = useState<Record<string, string>>({});
  const [methods, setMethods] = useState<Method[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const invoices = useOpenInvoices(account?.id ?? null, form.currency);

  useEffect(() => { if (isOpen) get<{ data: { payment_methods: Method[] } }>("/payment-methods").then((r) => setMethods(r.data.payment_methods)).catch(() => undefined); }, [isOpen]);
  useEffect(() => { if (isOpen) { setAccount(null); setAlloc({}); setErrors({}); setForm((f) => ({ ...f, amount: "", reference: "", notes: "" })); } }, [isOpen]);

  const allocated = Object.values(alloc).reduce((n, v) => n + Number(v || 0), 0);
  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setBusy(true);
    setErrors({});
    try {
      await post("/payments", {
        ...form, account_id: account?.id, payment_method_id: form.payment_method_id || null, reference: form.reference || null, notes: form.notes || null,
        allocations: Object.entries(alloc).filter(([, v]) => Number(v) > 0).map(([invoice_id, amount]) => ({ invoice_id, amount })),
      });
      notify("Payment recorded");
      onSaved();
      onClose();
    } catch (e) {
      setErrors(fieldErrors(e));
      notify(apiMessage(e, "Could not record the payment."));
    } finally { setBusy(false); }
  };

  return (
    <FloatingDrawer isOpen={isOpen} onClose={onClose} wide>
      <DrawerHeader title="Record a Payment" subtitle="Money received from an account; apply it to one or more of its invoices" />
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        <div><label className={darkLabel}>Account *</label><RecordSelect kind="accounts" value={account} onChange={(v) => { setAccount(v); setAlloc({}); }} />{errors.account_id && <p className="mt-1 text-xs text-red-400">{errors.account_id}</p>}</div>
        <div className="grid grid-cols-3 gap-3">
          <div><label className={darkLabel}>Amount *</label><input type="number" min="0" step="0.01" className={darkInput} value={form.amount} onChange={(e) => set("amount", e.target.value)} /></div>
          <div><label className={darkLabel}>Currency</label><AppSelect className={darkInput} value={form.currency} onChange={(e) => { set("currency", e.target.value); setAlloc({}); }}>{[...new Set([form.currency, base, "UGX", "USD", "EUR", "GBP"])].map((c) => <option key={c}>{c}</option>)}</AppSelect></div>
          <div><label className={darkLabel}>Received on *</label><DateInput max={new Date().toISOString().slice(0, 10)} className={darkInput} value={form.paid_date} onChange={(e) => set("paid_date", e.target.value)} /></div>
        </div>
        {errors.amount && <p className="text-xs text-red-400">{errors.amount}</p>}
        <div className="grid grid-cols-2 gap-4">
          <div><label className={darkLabel}>Method</label><AppSelect className={darkInput} value={form.payment_method_id} onChange={(e) => set("payment_method_id", e.target.value)}><option value="">Not specified</option>{methods.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}</AppSelect></div>
          <div><label className={darkLabel}>Reference</label><input className={darkInput} value={form.reference} onChange={(e) => set("reference", e.target.value)} placeholder="Transaction / receipt no." /></div>
        </div>
        {account && (
          <div>
            <p className={darkLabel}>Apply to invoices ({form.currency})</p>
            {invoices.length === 0 ? <p className="text-xs text-gray-400">No open {form.currency} invoices for this account. The payment is kept as credit you can apply later.</p> : (
              <div className="space-y-1.5">
                {invoices.map((i) => (
                  <div key={i.id} className="grid grid-cols-[1fr_auto_8rem] items-center gap-2 rounded-lg bg-gray-900 px-3 py-2 text-xs">
                    <span>{i.invoice_number} <span className="text-gray-400">· due {shortDate(i.due_date)}</span></span>
                    <button type="button" className="text-primary" onClick={() => setAlloc((a) => ({ ...a, [i.id]: i.balance }))}>{money(i.balance, i.currency)}</button>
                    <input type="number" min="0" step="0.01" className={darkInput} placeholder="0" value={alloc[i.id] ?? ""} onChange={(e) => setAlloc((a) => ({ ...a, [i.id]: e.target.value }))} />
                  </div>
                ))}
                <p className={`text-xs ${allocated > Number(form.amount || 0) ? "text-red-400" : "text-gray-400"}`}>Applied {money(allocated, form.currency)} of {money(form.amount || 0, form.currency)}{allocated < Number(form.amount || 0) ? ` · ${money(Number(form.amount) - allocated, form.currency)} stays as credit` : ""}</p>
              </div>
            )}
            {Object.keys(errors).filter((k) => k.startsWith("allocations")).map((k) => <p key={k} className="text-xs text-red-400">{errors[k]}</p>)}
          </div>
        )}
        <div><label className={darkLabel}>Notes</label><textarea rows={2} className={`${darkInput} resize-none`} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></div>
      </div>
      <div className="p-6 border-t border-gray-800 space-y-3">
        <button onClick={submit} disabled={busy || !account || !form.amount || allocated > Number(form.amount || 0)} className={darkPrimary}><Save className="w-4 h-4" />{busy ? "Saving..." : "Record Payment"}</button>
        <button onClick={onClose} className={darkSecondary}>Cancel</button>
      </div>
    </FloatingDrawer>
  );
}

function PaymentDrawer({ id, onClose, onChanged }: { id: string | null; onClose: () => void; onChanged: () => void }) {
  const [p, setP] = useState<Payment | null>(null);
  const [view, setView] = useState<"details" | "refund" | "apply">("details");
  const [refund, setRefund] = useState({ amount: "", refunded_date: new Date().toISOString().slice(0, 10), reason: "" });
  const [apply, setApply] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const invoices = useOpenInvoices(view === "apply" ? p?.account?.id ?? null : null, p?.currency ?? "");

  const load = useCallback(async () => {
    if (!id) { setP(null); return; }
    const r = await get<{ data: { payment: Payment } }>(`/payments/${id}`).catch(() => null);
    setP(r?.data.payment ?? null);
  }, [id]);
  useEffect(() => { setView("details"); load(); }, [load]);

  const doRefund = async () => {
    if (!p) return;
    setBusy(true);
    try {
      await post(`/payments/${p.id}/refunds`, refund);
      notify("Refund recorded. The invoices it paid are open again.");
      setView("details");
      load();
      onChanged();
    } catch (e) { notify(apiMessage(e, "Could not record the refund.")); }
    finally { setBusy(false); }
  };
  const doApply = async () => {
    if (!p) return;
    setBusy(true);
    try {
      await post(`/payments/${p.id}/allocations`, { allocations: Object.entries(apply).filter(([, v]) => Number(v) > 0).map(([invoice_id, amount]) => ({ invoice_id, amount })) });
      notify("Credit applied");
      setApply({});
      setView("details");
      load();
      onChanged();
    } catch (e) { notify(apiMessage(e, "Could not apply the credit.")); }
    finally { setBusy(false); }
  };

  const refundable = p ? Number(p.amount) - Number(p.refunded_amount) : 0;

  return (
    <FloatingDrawer isOpen={!!id} onClose={onClose}>
      {p && view === "details" && (
        <>
          <DrawerHeader title="Payment" subtitle={`${money(p.amount, p.currency)} from ${p.account?.name ?? "—"}`} />
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            <span className={pill(STATUS[p.status] ?? STATUS.succeeded)}>{STATUS_LABEL[p.status] ?? label(p.status)}</span>
            <DetailRow icon={Building2} label="Account">{p.account?.name ?? "—"}</DetailRow>
            <DetailRow icon={Calendar} label="Received on">{shortDate(p.paid_date)}</DetailRow>
            <DetailRow icon={CreditCard} label="Method">{p.payment_method?.name ?? p.method ?? (p.provider ? label(p.provider) : "—")}</DetailRow>
            {p.reference && <DetailRow icon={Hash} label="Reference">{p.reference}</DetailRow>}
            {p.recorder && <DetailRow icon={User} label="Recorded by">{p.recorder.first_name} {p.recorder.last_name}</DetailRow>}
            {Number(p.refunded_amount) > 0 && <DetailRow icon={Undo2} label="Refunded">{money(p.refunded_amount, p.currency)}</DetailRow>}
            <div>
              <p className="mb-2 flex items-center gap-2 text-xs font-semibold text-white"><FileText className="w-4 h-4" />Applied to</p>
              {(p.allocations ?? []).length === 0 ? <p className="text-xs text-gray-400">Not applied to an invoice yet.</p> : p.allocations!.map((a) => (
                <div key={a.id} className="flex justify-between rounded-lg bg-gray-900 px-3 py-2 text-xs mb-1.5">
                  {a.invoice ? <Link to={`/invoices/${a.invoice.id}`} className="text-primary">{a.invoice.invoice_number}</Link> : <span>—</span>}
                  <span>{money(a.amount, p.currency)}</span>
                </div>
              ))}
              {Number(p.unallocated_amount) > 0 && <p className="mt-1 text-xs text-amber-400">{money(p.unallocated_amount, p.currency)} not yet applied (credit).</p>}
            </div>
            {p.notes && <DetailRow icon={FileText} label="Notes">{p.notes}</DetailRow>}
          </div>
          <div className="p-3 border-t border-gray-800 space-y-2">
            {Number(p.unallocated_amount) > 0 && can("payments.create") && <button onClick={() => setView("apply")} className={darkPrimary}>Apply credit to an invoice</button>}
            {refundable > 0 && ["succeeded", "partially_refunded"].includes(p.status) && can("payments.refund") && <button onClick={() => { setRefund((r) => ({ ...r, amount: String(refundable) })); setView("refund"); }} className={darkSecondary}><Undo2 className="inline w-4 h-4 mr-1" />Record a refund</button>}
          </div>
        </>
      )}
      {p && view === "refund" && (
        <>
          <DrawerHeader title="Record a Refund" subtitle={`Up to ${money(refundable, p.currency)}. The invoices this payment settled are reopened by the refunded amount.`} />
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><label className={darkLabel}>Amount *</label><input type="number" min="0" step="0.01" max={refundable} className={darkInput} value={refund.amount} onChange={(e) => setRefund({ ...refund, amount: e.target.value })} /></div>
              <div><label className={darkLabel}>Refunded on *</label><DateInput className={darkInput} value={refund.refunded_date} onChange={(e) => setRefund({ ...refund, refunded_date: e.target.value })} /></div>
            </div>
            <div><label className={darkLabel}>Reason *</label><textarea rows={3} className={`${darkInput} resize-none`} value={refund.reason} onChange={(e) => setRefund({ ...refund, reason: e.target.value })} /></div>
          </div>
          <div className="p-6 border-t border-gray-800 space-y-3">
            <button onClick={doRefund} disabled={busy || !refund.amount || !refund.reason} className={darkPrimary}>{busy ? "Saving..." : "Record Refund"}</button>
            <button onClick={() => setView("details")} className={darkSecondary}>Back</button>
          </div>
        </>
      )}
      {p && view === "apply" && (
        <>
          <DrawerHeader title="Apply Credit" subtitle={`${money(p.unallocated_amount, p.currency)} available`} />
          <div className="flex-1 overflow-y-auto p-6 space-y-2">
            {invoices.length === 0 && <p className="text-xs text-gray-400">This account has no open {p.currency} invoices.</p>}
            {invoices.map((i) => (
              <div key={i.id} className="grid grid-cols-[1fr_auto_8rem] items-center gap-2 rounded-lg bg-gray-900 px-3 py-2 text-xs">
                <span>{i.invoice_number}</span>
                <span className="text-gray-400">{money(i.balance, i.currency)}</span>
                <input type="number" min="0" step="0.01" className={darkInput} value={apply[i.id] ?? ""} onChange={(e) => setApply((a) => ({ ...a, [i.id]: e.target.value }))} />
              </div>
            ))}
          </div>
          <div className="p-6 border-t border-gray-800 space-y-3">
            <button onClick={doApply} disabled={busy || !Object.values(apply).some((v) => Number(v) > 0)} className={darkPrimary}>{busy ? "Saving..." : "Apply"}</button>
            <button onClick={() => setView("details")} className={darkSecondary}>Back</button>
          </div>
        </>
      )}
    </FloatingDrawer>
  );
}

export default function PaymentsPage() {
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const [sort, setSort] = useState("-paid_date");
  const list = useRemoteList<Payment>("/payments", { search: query, status, sort });
  const { counts, loading: countsLoading } = useCounts("/payments", {
    received: { status: "succeeded,partially_refunded" }, pending: { status: "pending" }, refunded: { status: "refunded,partially_refunded" },
  }, list.rows.length);
  const finance = useApiData<{ by_currency: Array<{ currency: string; collected_this_month: string }>; display: { currency: string; collected_this_month: string } | null }>("/invoices/stats", can("invoices.view"), list.rows.length);
  const collected = finance?.display ?? finance?.by_currency[0] ?? null;
  const [selected, setSelected] = useState<string | null>(null);
  const [recordOpen, setRecordOpen] = useState(false);

  useEffect(() => { const t = setTimeout(() => setQuery(search.trim()), 300); return () => clearTimeout(t); }, [search]);

  const columns: ColumnDef<Payment>[] = [
    { id: "account", header: "Account", cell: ({ row }) => (<div><p className="text-xs font-semibold text-gray-900">{row.original.account?.name ?? "—"}</p><p className="text-xs text-gray-500">{row.original.reference ?? ""}</p></div>) },
    { accessorKey: "amount", header: "Amount", cell: ({ row }) => <span className="text-xs font-semibold text-gray-900">{money(row.original.amount, row.original.currency)}</span> },
    { accessorKey: "paid_date", header: "Received", cell: ({ row }) => <span className="text-xs text-gray-600">{shortDate(row.original.paid_date)}</span> },
    { id: "method", header: "Method", cell: ({ row }) => <span className="text-xs text-gray-600">{row.original.payment_method?.name ?? row.original.method ?? "—"}</span> },
    { id: "credit", header: "Unapplied", cell: ({ row }) => <span className={`text-xs ${Number(row.original.unallocated_amount) > 0 ? "font-semibold text-amber-600" : "text-gray-400"}`}>{Number(row.original.unallocated_amount) > 0 ? money(row.original.unallocated_amount, row.original.currency) : "—"}</span> },
    { accessorKey: "status", header: "Status", cell: ({ row }) => <span className={pill(STATUS[row.original.status] ?? STATUS.succeeded)}>{STATUS_LABEL[row.original.status] ?? label(row.original.status)}</span> },
  ];

  return (
    <div className="min-h-screen p-4 sm:p-0">
      <div className="w-full">
        <PageHeader title="Payments" description="Money received, how it was applied, and refunds" />
        <ListShell
          columns={columns}
          rows={list.rows}
          loading={list.loading}
          pagination={list.pagination}
          onPage={list.setPage}
          onPerPage={list.setPerPage}
          noun="payments"
          onRowClick={(p) => setSelected(p.id)}
          grid={{
            id: "payments",
            search: <SearchInput className="w-full sm:w-64" placeholder="Search account or reference…" value={search} onChange={(e) => setSearch(e.target.value)} />,
            filters: (
              <label className="text-xs text-gray-600">Status
                <AppSelect className={`${toolbarInput} mt-1`} value={status} onChange={(e) => setStatus(e.target.value)}><option value="">All statuses</option>{Object.keys(STATUS).map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}</AppSelect>
              </label>
            ),
            activeFilters: status ? 1 : 0,
            onClearFilters: () => setStatus(""),
            sortOptions: [
              { value: "-paid_date", label: "Newest received" }, { value: "paid_date", label: "Oldest received" },
              { value: "-amount", label: "Largest amount" }, { value: "amount", label: "Smallest amount" },
            ],
            sort, onSort: setSort,
            stats: [
              ...(collected ? [{ label: "Collected this month", value: money(collected.collected_this_month, collected.currency), tone: "good" as const }] : []),
              { label: "Payments received", value: countText(counts.received) },
              { label: "Pending", value: countText(counts.pending), hint: "Awaiting confirmation", tone: counts.pending ? "warn" as const : "default" as const },
              { label: "With refunds", value: countText(counts.refunded), hint: "Fully or partly refunded" },
            ],
            statsLoading: countsLoading,
            exportEntity: "payments", exportPermission: "invoices.export",
          }}
          onAdd={can("payments.create") ? () => setRecordOpen(true) : undefined}
          addLabel="Record Payment"
          empty={{ icon: Wallet, title: "No payments yet", text: "Payments recorded on invoices, or here, appear in this list." }}
        />
      </div>
      <PaymentDrawer id={selected} onClose={() => setSelected(null)} onChanged={list.reload} />
      <RecordPayment isOpen={recordOpen} onClose={() => setRecordOpen(false)} onSaved={list.reload} />
    </div>
  );
}
